import { observable, action, computed } from 'mobx';
import { IUserLocation, UserLocation } from '~domain/entities/UserLocation';
import { BrowserStorage } from '@estee/elc-universal-utils';
import { akaLocalizationCookieName } from '~constants/constants';
import { GoogleMapsApi } from '../../api/GoogleMapsApi';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { Config } from '~domain/entities/Config';
import { LocationCache } from '~api/LocationCacheService';
import { IAddressSearch, IStoresCoordinates } from '~interfaces/IStoresInterfaces';

export interface INavigatorCoordinates {
    latitude: number;
    longitude: number;
}

export interface INavigatorPosition {
    coords: INavigatorCoordinates;
}

export interface IBrowserLocation {
    lat: number;
    lng: number;
}

export class UserLocationRepository {
    @observable public userLocation: UserLocation;
    @observable public fullAddress: IAddressSearch;
    @observable public configService: Config;
    @observable public locationPopUpDisabled: boolean = false;
    public mapApi: GoogleMapsApi;

    constructor(userLocation: UserLocation, config: Config) {
        this.userLocation = userLocation;
        this.configService = config;
        this.mapApi = diContainer.get(serviceNames.googleMapsApi);
        this.locationPopUpDisabled = config?.config?.featureFlags?.disableLocationPop;
    }

    private getAkamaiUserLocation = () => {
        const localizationData = BrowserStorage.getCookie(akaLocalizationCookieName) || '';

        if (localizationData) {
            const [latitude, longitude] = localizationData.split(',');
            const lat = Number(latitude.substring(latitude.indexOf('=') + 1));
            const lng = Number(longitude.substring(longitude.indexOf('=') + 1));

            return { lat, lng };
        }
    };

    private getBrowserCurrentPosition = (): Promise<INavigatorPosition> => {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        });
    };

    private getBrowserUserLocation = async () => {
        try {
            const cacheBrowserLocation = LocationCache.getUserBrowserLocation();
            const cacheZipCode = LocationCache.getUserZipCode();

            if (cacheBrowserLocation && cacheZipCode) {
                return cacheBrowserLocation;
            }

            let position: INavigatorPosition;

            if (this.locationPopUpDisabled) {
                const location = this.getAkamaiUserLocation();
                if (location?.lat && location?.lng) {
                    position = { coords: { latitude: location.lat, longitude: location.lng } };
                } else {
                    return location;
                }
            } else {
                position = await this.getBrowserCurrentPosition();
            }

            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            LocationCache.setUserBrowserLocation({ lat, lng });

            return { lat, lng };
        } catch (error) {
            return this.getAkamaiUserLocation();
        }
    };

    public fetchUserZipCode = async (latlng: IStoresCoordinates) => {
        try {
            const response = await this.mapApi.getAdressFromCoordinates(latlng);
            if (typeof response.results[0] !== 'undefined') {
                const { extractZipcodeRegexp } = this.configService.config;
                const zipcode = response.results[0].formatted_address.match(
                    new RegExp(extractZipcodeRegexp) || /\d{5}-\d{4}|\d{5}/
                );
                if (zipcode) {
                    return zipcode[1] || zipcode[0];
                } else {
                    throw new Error('Unable to look-up postal code');
                }
            } else {
                throw new Error('Unable to look-up geolocation.');
            }
        } catch (error) {
            console.error(error);
        }
    };

    private async getUserZipCode(browserLocation: { lat: number; lng: number } | undefined) {
        let zipcode: string | undefined;
        const cacheZipCode = LocationCache.getUserZipCode();
        if (cacheZipCode) {
            return LocationCache.getUserZipCode();
        }
        if (browserLocation) {
            zipcode = await this.fetchUserZipCode(browserLocation);
        }
        if (zipcode) {
            LocationCache.setUserZipCode(zipcode);
        }

        return zipcode || '';
    }

    @action
    public saveFullAddress = async (searchAddress: IAddressSearch) => {
        const { lat, lng, postal_code } = searchAddress;
        this.fullAddress = searchAddress;
        this.userLocation.update({
            geolocation: { lat, lng },
            zipcode: postal_code
        });
        LocationCache.setUserFullAddress(searchAddress);
        LocationCache.setUserBrowserLocation({ lat, lng });
        LocationCache.setUserZipCode(postal_code || '');
    };

    @action
    public resetUserLocation = async (position: INavigatorPosition) => {
        const {
            coords: { latitude: lat, longitude: lng }
        } = position;
        const zipcode = await this.fetchUserZipCode({ lat, lng });
        this.userLocation.update({
            geolocation: { lat, lng },
            zipcode: zipcode
        });
        LocationCache.setUserBrowserLocation({ lat, lng });
        LocationCache.setUserZipCode(zipcode || '');
    };

    @action
    public updateUserLocation = (address: IUserLocation) => {
        LocationCache.setUserZipCode(address.zipcode);
        LocationCache.setUserBrowserLocation(address.geolocation);
        this.userLocation.update(address);
    };

    @action
    public getUserLocation = async () => {
        if (navigator.geolocation) {
            const browserLocation = await this.getBrowserUserLocation();
            const zipcode = await this.getUserZipCode(browserLocation);
            this.userLocation.update({
                geolocation: browserLocation,
                zipcode: zipcode
            });
        } else {
            this.userLocation.update({
                geolocation: this.getAkamaiUserLocation()
            });
        }
    };

    @computed
    public get isLocationAvailable() {
        const { geolocation } = this.userLocation;

        return !!(geolocation && geolocation.lat && this.userLocation.geolocation.lng);
    }

    public fetchGeolocationPermissions = async (): Promise<PermissionState> => {
        const permissions = await navigator.permissions?.query({ name: 'geolocation' });

        return permissions?.state;
    };

    @action
    public saveUserSearchedLocation = (searchedLocation: IAddressSearch) =>
        LocationCache.setUserFullAddress(searchedLocation);

    @action
    public getUserSearchedLocation = () => {
        const userFullLocation = LocationCache.getUserFullAddress();

        return userFullLocation ? userFullLocation : null;
    };
}
